import type { SessionKind } from '@pingo/shared';
import type { ZodType } from 'zod';
import { clearSession, getSession } from './auth-store';
import { isEmbeddedApp, postToApp } from './embedded';

/**
 * API origin. An empty / unset VITE_API_BASE_URL means "same origin" in production builds (the
 * Caddy edge proxies /api/*), so one web image serves any domain; dev falls back to the api port.
 */
export const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  (import.meta.env.PROD && typeof window !== 'undefined'
    ? window.location.origin
    : 'http://localhost:4610');

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** true for network failures (offline) — callers like the caddy queue retry these */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export interface ApiOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** which stored session to send (default USER if present). 'none' = anonymous */
  auth?: SessionKind | 'none';
  query?: Record<string, string | number | boolean | string[] | undefined | null>;
  /** optional zod schema to validate the response (from @pingo/shared) */
  schema?: ZodType<T>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export function buildUrl(path: string, query?: ApiOptions<unknown>['query']): string {
  const url = new URL(path.startsWith('http') ? path : `${API_BASE_URL}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) v.forEach((item) => url.searchParams.append(k, item));
      else url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export function authHeader(kind: SessionKind | 'none' = 'USER'): Record<string, string> {
  if (kind === 'none') return {};
  const s = getSession(kind);
  return s ? { Authorization: `Bearer ${s.token}` } : {};
}

/** 401 codes that mean the bearer session itself is invalid / expired / revoked. */
export function isSessionRejection(code: string): boolean {
  return code === 'UNAUTHENTICATED' || code.startsWith('HTTP_');
}

/** Parse an error response into ApiError; ends the stored session only for a dead session. */
async function apiErrorOf(res: Response, kind: SessionKind | 'none'): Promise<ApiError> {
  let code = 'HTTP_' + res.status;
  let message = res.statusText;
  let details: unknown;
  try {
    const j = (await res.json()) as {
      error?: { code: string; message: string; details?: unknown };
    };
    if (j.error) ({ code, message, details } = j.error);
  } catch {
    /* not json */
  }
  // only a dead session ends the stored session: a 401 for a wrong sign-off code or a wrong
  // current password (INVALID_CREDENTIALS, SIGN_OFF_CODE_INVALID) must not log the user out
  if (res.status === 401 && kind !== 'none' && isSessionRejection(code)) clearSession(kind);
  if (res.status === 403 && code === 'PASSWORD_CHANGE_REQUIRED' && typeof window !== 'undefined')
    window.dispatchEvent(new Event('pingo:password-change-required'));
  return new ApiError(res.status, code, message, details);
}

/**
 * Typed fetch wrapper for the PinGo API (paths start with /api/v1/...).
 * Errors are thrown as ApiError(code) using the server's { error: { code, message } } shape.
 */
export async function api<T = unknown>(path: string, opts: ApiOptions<T> = {}): Promise<T> {
  const kind = opts.auth ?? 'USER';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...authHeader(kind),
    ...opts.headers,
  };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers,
      body,
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK', (err as Error).message);
  }
  if (!res.ok) throw await apiErrorOf(res, kind);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('application/json') ? await res.json() : await res.blob();
  return opts.schema ? opts.schema.parse(data) : (data as T);
}

/**
 * Download a binary export (PDF/XLSX) with auth and trigger a browser save. Inside the PinGo app
 * (lib/embedded.ts) a WebView cannot save a blob: URL, so the app is asked to fetch the same api
 * path with its own USER session and open the share sheet (§15 #402).
 */
export async function downloadFile(
  path: string,
  fileName: string,
  opts: Omit<ApiOptions<Blob>, 'schema'> = {},
): Promise<void> {
  if (isEmbeddedApp()) {
    const u = new URL(buildUrl(path, opts.query));
    if (postToApp({ type: 'pingo.download', path: u.pathname + u.search, filename: fileName }))
      return;
  }
  const blob = await api<Blob>(path, { ...opts, headers: { Accept: '*/*', ...opts.headers } });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
